import React, { useMemo, useState, Component } from 'react';
import { Cake, Phone, Users, UserCheck, CalendarDays, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { mockEmployees, mockCandidates, mockInterviews } from '../../utils/mockData';
import { Candidate } from '../../types/hr.types';
type EventKind = 'birthday' | 'phone' | 'interview' | 'starting';
interface AgendaEvent {
  id: string;
  kind: EventKind;
  date: Date;
  title: string;
  subtitle?: string;
  candidate?: Candidate;
}
interface UpcomingScheduleWidgetProps {
  onCandidateClick?: (candidate: Candidate) => void;
  daysAhead?: number;
}
const KIND_META: Record<EventKind, {
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  chip: string;
  iconBg: string;
  iconText: string;
}> = {
  birthday: {
    label: 'Birthday',
    icon: Cake,
    chip: 'bg-pink-50 text-pink-700 ring-pink-100',
    iconBg: 'bg-pink-100',
    iconText: 'text-pink-600'
  },
  phone: {
    label: 'Phone Screen',
    icon: Phone,
    chip: 'bg-purple-50 text-purple-700 ring-purple-100',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600'
  },
  interview: {
    label: 'Interview',
    icon: Users,
    chip: 'bg-blue-50 text-blue-700 ring-blue-100',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600'
  },
  starting: {
    label: 'Starting',
    icon: UserCheck,
    chip: 'bg-green-50 text-green-700 ring-green-100',
    iconBg: 'bg-green-100',
    iconText: 'text-green-600'
  }
};
const FILTER_OPTIONS: {
  id: 'all' | EventKind;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'interview',
  label: 'Interviews'
}, {
  id: 'phone',
  label: 'Phone'
}, {
  id: 'starting',
  label: 'Starting'
}, {
  id: 'birthday',
  label: 'Birthdays'
}];
function startOfDay(d: Date) {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
}
function formatDayHeader(d: Date): string {
  const today = startOfDay(new Date());
  const target = startOfDay(d);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff < 7) return d.toLocaleDateString(undefined, {
    weekday: 'long'
  });
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}
function formatTime(d: Date): string | null {
  if (d.getHours() === 0 && d.getMinutes() === 0) return null;
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
}
export function UpcomingScheduleWidget({
  onCandidateClick,
  daysAhead = 30
}: UpcomingScheduleWidgetProps) {
  const [filter, setFilter] = useState<'all' | EventKind>('all');
  const events = useMemo<AgendaEvent[]>(() => {
    const today = startOfDay(new Date());
    const end = new Date(today);
    end.setDate(today.getDate() + daysAhead);
    const list: AgendaEvent[] = [];
    // Birthdays
    mockEmployees.forEach(e => {
      if (!e.isActive) return;
      const [m, d] = e.birthday.split('-').map(Number);
      // Build this year's occurrence; if past, roll to next year.
      const year = today.getFullYear();
      let occurrence = new Date(year, m - 1, d);
      if (startOfDay(occurrence).getTime() < today.getTime()) {
        occurrence = new Date(year + 1, m - 1, d);
      }
      if (occurrence <= end) {
        list.push({
          id: `bd-${e.id}`,
          kind: 'birthday',
          date: occurrence,
          title: e.name,
          subtitle: e.department
        });
      }
    });
    // Phone screens & interviews from mockInterviews
    mockInterviews.forEach(i => {
      const d = new Date(i.dateTime);
      if (d < today || d > end) return;
      const kind: EventKind = i.type === 'In-Person' ? 'interview' : 'phone';
      list.push({
        id: `iv-${i.id}`,
        kind,
        date: d,
        title: i.candidateName,
        subtitle: `${i.role} • ${i.interviewer}`
      });
    });
    // Candidate phone screens / interviews from candidate pipeline
    mockCandidates.forEach(c => {
      const phoneDate = c.phoneInterviewDate ? new Date(c.phoneInterviewDate) : null;
      if (phoneDate && !isNaN(phoneDate.getTime()) && phoneDate >= today && phoneDate <= end && c.status === 'Phone Screen') {
        list.push({
          id: `cps-${c.id}`,
          kind: 'phone',
          date: phoneDate,
          title: `${c.firstName} ${c.lastName}`,
          subtitle: `${c.role} • ${c.recruiter || c.interviewedBy}`,
          candidate: c
        });
      }
      const inPerson = c.inPersonInterviewDate ? new Date(c.inPersonInterviewDate) : null;
      if (inPerson && !isNaN(inPerson.getTime()) && inPerson >= today && inPerson <= end && c.status === 'In Person Interview') {
        list.push({
          id: `civ-${c.id}`,
          kind: 'interview',
          date: inPerson,
          title: `${c.firstName} ${c.lastName}`,
          subtitle: `${c.role} • ${c.recruiter || c.interviewedBy}`,
          candidate: c
        });
      }
      // Candidates starting (Pre-boarding / Ready to Start) — use desiredStartDate
      if ((c.status === 'Pre-boarding' || c.status === 'Ready to Start') && c.desiredStartDate) {
        const startDate = new Date(c.desiredStartDate);
        if (!isNaN(startDate.getTime()) && startDate >= today && startDate <= end) {
          list.push({
            id: `cs-${c.id}`,
            kind: 'starting',
            date: startDate,
            title: `${c.firstName} ${c.lastName}`,
            subtitle: `Starting as ${c.role}`,
            candidate: c
          });
        }
      }
    });
    return list.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [daysAhead]);
  const filteredEvents = filter === 'all' ? events : events.filter(e => e.kind === filter);
  // Group by day
  const grouped = useMemo(() => {
    const map = new Map<string, AgendaEvent[]>();
    filteredEvents.forEach(e => {
      const key = startOfDay(e.date).toISOString();
      const arr = map.get(key) || [];
      arr.push(e);
      map.set(key, arr);
    });
    return Array.from(map.entries()).map(([key, items]) => ({
      day: new Date(key),
      items
    }));
  }, [filteredEvents]);
  return <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-full" data-id="element-1657">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-orange-50/40 to-transparent" data-id="element-1658">
        <div className="flex items-center gap-2" data-id="element-1659">
          <div className="p-1.5 rounded-lg bg-orange-100" data-id="element-1660">
            <CalendarDays className="h-5 w-5 text-orange-600" data-id="element-1661" />
          </div>
          <div data-id="element-1662">
            <h3 className="font-semibold text-gray-900" data-id="element-1663">Upcoming Schedule</h3>
            <p className="text-[11px] text-gray-500" data-id="element-1664">Next {daysAhead} days</p>
          </div>
        </div>
        <span className="bg-white text-gray-700 text-xs font-bold px-2.5 py-1 rounded-full shadow-sm border border-gray-100" data-id="element-1665">
          {filteredEvents.length}
        </span>
      </div>

      {/* Filter chips */}
      <div className="px-4 pt-3 pb-2 flex gap-1.5 overflow-x-auto" data-id="element-1666">
        {FILTER_OPTIONS.map(opt => {
        const active = filter === opt.id;
        return <button key={opt.id} onClick={() => setFilter(opt.id)} className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${active ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-200' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-id="element-1667">
              {opt.label}
            </button>;
      })}
      </div>

      <div className="flex-1 overflow-y-auto p-4 pt-2 space-y-5" data-id="element-1668">
        {grouped.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center py-10" data-id="element-1669">
            <div className="h-12 w-12 rounded-full bg-gray-50 flex items-center justify-center mb-3" data-id="element-1670">
              <CalendarDays className="h-6 w-6 text-gray-400" data-id="element-1671" />
            </div>
            <p className="text-sm font-medium text-gray-900" data-id="element-1672">
              Nothing upcoming
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1673">
              No{' '}
              {filter === 'all' ? '' : FILTER_OPTIONS.find(o => o.id === filter)?.label.toLowerCase() + ' '}
              events in the next {daysAhead} days.
            </p>
          </div> : grouped.map(({
        day,
        items
      }) => <div key={day.toISOString()} data-id="element-1674">
              <div className="flex items-baseline gap-2 mb-2 sticky top-0 bg-white pb-1" data-id="element-1675">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-900" data-id="element-1676">
                  {formatDayHeader(day)}
                </h4>
                <span className="text-[11px] text-gray-400" data-id="element-1677">
                  {day.toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric'
            })}
                </span>
              </div>
              <div className="space-y-2" data-id="element-1678">
                {items.map((ev, idx) => {
            const meta = KIND_META[ev.kind];
            const Icon = meta.icon;
            const time = formatTime(ev.date);
            const clickable = !!ev.candidate && !!onCandidateClick;
            return <motion.button key={ev.id} initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.02
            }} onClick={() => clickable && onCandidateClick!(ev.candidate!)} disabled={!clickable} className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 bg-white transition-all ${clickable ? 'hover:border-gray-200 hover:shadow-sm cursor-pointer' : 'cursor-default'}`} data-id="element-1679">
                      <div className={`h-9 w-9 rounded-lg ${meta.iconBg} ${meta.iconText} flex items-center justify-center flex-shrink-0`} data-id="element-1680">
                        <Icon className="h-4 w-4" data-id="element-1681" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-1682">
                        <div className="flex items-center gap-2" data-id="element-1683">
                          <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-1684">
                            {ev.title}
                          </p>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset ${meta.chip}`} data-id="element-1685">
                            {meta.label}
                          </span>
                        </div>
                        {ev.subtitle && <p className="text-xs text-gray-500 truncate" data-id="element-1686">
                            {ev.subtitle}
                          </p>}
                      </div>
                      <div className="text-right flex-shrink-0" data-id="element-1687">
                        {time && <p className="text-xs font-semibold text-gray-700" data-id="element-1688">
                            {time}
                          </p>}
                        {clickable && <ChevronRight className="inline h-3.5 w-3.5 text-gray-300 ml-1" data-id="element-1689" />}
                      </div>
                    </motion.button>;
          })}
              </div>
            </div>)}
      </div>
    </div>;
}